import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Barcode, Check } from "lucide-react";

/**
 * Unmatched FRS descriptions, shown before the import is applied.
 *
 * Coding a row saves it to the acronym master, so the same wording matches by itself on every
 * later upload. Nothing here blocks the import — a row left alone keeps its full description.
 */
export default function FrsCodeAssignPanel({ report, onAssigned }) {
  const [acronyms, setAcronyms] = useState([]);
  const [draft, setDraft] = useState({});
  const [done, setDone] = useState({});

  useEffect(() => {
    api.get("/item-acronyms").then((r) => setAcronyms(r.data.rows || [])).catch(() => {});
  }, []);

  const rows = report?.unmatched || [];
  if (!rows.length) {
    return (
      <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800"
           data-testid="frs-codes-all-matched">
        <Check className="inline w-3.5 h-3.5 mr-1" />
        Every item matched the FRS code master ({report?.matched || 0} of {report?.total || 0} rows).
      </div>
    );
  }

  const key = (r) => `${r.part_number}|${r.description}`;
  const set = (r, field, value) =>
    setDraft((d) => ({ ...d, [key(r)]: { ...(d[key(r)] || {}), [field]: value } }));

  const assign = async (r) => {
    const d = draft[key(r)] || {};
    if (!(d.acronym || "").trim()) return toast.error("Pick or type an acronym");
    try {
      const res = await api.post("/item-codes/assign", {
        description: r.description, part_number: r.part_number,
        acronym: d.acronym.trim().toUpperCase(), size: (d.size || "").trim(),
        color: (d.color || "").trim(), means: (d.means || "").trim(),
      });
      setDone((x) => ({ ...x, [key(r)]: res.data.short }));
      onAssigned?.();
      toast.success(`Coded as ${res.data.short}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save that code");
    }
  };

  const left = rows.filter((r) => !done[key(r)]).length;

  return (
    <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-3 space-y-2"
         data-testid="frs-code-assign-panel">
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-amber-900">
        <Barcode className="w-3.5 h-3.5" />
        Unmatched item codes ({left})
        <span className="ml-auto font-normal normal-case text-amber-800">
          {report.matched} of {report.total} rows already coded
        </span>
      </div>
      <p className="text-[11px] text-amber-800">
        Give each one an acronym and it is saved to the master, so it codes itself next time.
        Anything you skip keeps its full description.
      </p>
      <datalist id="frs-acronym-options">
        {acronyms.map((a) => <option key={a.id} value={a.acronym}>{a.means}</option>)}
      </datalist>
      <div className="max-h-56 overflow-auto space-y-1">
        {rows.map((r) => {
          const k = key(r);
          const d = draft[k] || {};
          return (
            <div key={k} className="flex flex-wrap items-center gap-1.5 rounded bg-white px-2 py-1.5"
                 data-testid={`frs-unmatched-row-${r.part_number || "na"}`}>
              <span className="font-mono text-[11px] text-gray-500 w-24 truncate" title={r.part_number}>
                {r.part_number || "—"}
              </span>
              <span className="flex-1 min-w-[160px] text-xs text-gray-800 truncate"
                    title={r.description}>{r.description}</span>
              <span className="text-[10px] text-gray-400">{r.rows} row{r.rows === 1 ? "" : "s"}</span>
              {done[k] ? (
                <span className="font-mono text-xs font-semibold text-emerald-700"
                      data-testid={`frs-unmatched-done-${r.part_number || "na"}`}>{done[k]}</span>
              ) : (
                <>
                  <input list="frs-acronym-options" value={d.acronym || ""}
                         onChange={(e) => set(r, "acronym", e.target.value.toUpperCase())}
                         placeholder="ACR" data-testid={`frs-assign-acronym-${r.part_number || "na"}`}
                         className="h-7 w-20 rounded border border-gray-300 px-1.5 font-mono text-xs uppercase" />
                  <input value={d.size || ""} onChange={(e) => set(r, "size", e.target.value)}
                         placeholder="size" className="h-7 w-16 rounded border border-gray-300 px-1.5 text-xs"
                         data-testid={`frs-assign-size-${r.part_number || "na"}`} />
                  <input value={d.color || ""} onChange={(e) => set(r, "color", e.target.value.toUpperCase())}
                         placeholder="finish" className="h-7 w-16 rounded border border-gray-300 px-1.5 text-xs uppercase"
                         data-testid={`frs-assign-color-${r.part_number || "na"}`} />
                  <Button size="sm" className="h-7 bg-amber-600 hover:bg-amber-700 text-white text-xs"
                          onClick={() => assign(r)}
                          data-testid={`frs-assign-save-${r.part_number || "na"}`}>Code it</Button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
